// Binding layer between the co-located manage-account slice and the presentational
// screen. The page keeps consuming `{ data, loading, error, refetch }`, so
// Manage.jsx and the reusable components are untouched — this hook just sources
// that from Redux.
//
// Orchestration: identity is owned by the app-global auth slice (bootstrapped once
// by <AuthProvider>; <RequireAuth> gates the page until authenticated). This hook
// reads the selected account from auth and (re)fetches /manage-account whenever
// that id changes. The multi-plan switcher only dispatches setSelectedAccount(id);
// this refetches automatically — no change needed here.
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../auth/AuthProvider';
import {
  fetchManageAccount,
  selectManageAccountData,
  selectManageAccountStatus,
  selectManageAccountError,
} from '../../../store/slices/manageAccount.slice';

export function useManageAccountData() {
  const dispatch = useDispatch();
  const { selectedAccountId: accountId, status: authStatus, error: authError } = useAuth();

  const data = useSelector(selectManageAccountData);
  const status = useSelector(selectManageAccountStatus);
  const dataError = useSelector(selectManageAccountError);

  // Mock mode resolves without a live session; live mode needs identity + a
  // selected account before there's anything to fetch.
  const shouldFetch = authStatus === 'authenticated' && !!accountId;

  // (Re)fetch once identity is ready and whenever the selected account changes.
  useEffect(() => {
    if (shouldFetch) dispatch(fetchManageAccount(accountId));
  }, [shouldFetch, accountId, dispatch]);

  const refetch = useCallback(() => {
    if (shouldFetch) dispatch(fetchManageAccount(accountId));
  }, [shouldFetch, accountId, dispatch]);

  // Loading covers: identity in flight, data in flight, or selection resolved but
  // its fetch hasn't started yet (avoids an empty-state flash before dispatch).
  const loading =
    authStatus === 'loading' ||
    (shouldFetch && (status === 'idle' || status === 'loading'));

  const error = authError || dataError || null;

  return { data, loading, error, refetch };
}

export default useManageAccountData;
